export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'refunded';

/** Statuses the office can still cancel. Paid / refunded invoices are settled money and stay locked. */
export const CANCELLABLE_INVOICE_STATUSES: InvoiceStatus[] = ['draft', 'sent', 'overdue'];
/**
 * An invoice with money against it can still be deleted (mistakes happen), but
 * only by users who can see company financials, and the confirmation says what
 * is lost: the payment rows survive with their invoice link cleared.
 */
export function invoiceDeleteIsRisky(invoice: { status: InvoiceStatus | string; amount_paid?: number | null }): boolean {
  return invoice.status === 'paid' || invoice.status === 'refunded' || Number(invoice.amount_paid ?? 0) > 0;
}

export function canDeleteInvoice(
  invoice: { status: InvoiceStatus | string; amount_paid?: number | null },
  canSeeFinancials: boolean,
): boolean {
  return canSeeFinancials || !invoiceDeleteIsRisky(invoice);
}

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

export interface Invoice {
  id: string;
  company_id: string;
  invoice_number: string;
  job_id: string | null;
  customer_id: string | null;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  customer_address: string | null;
  service_address: string | null;
  subtotal: number;
  gst_amount: number;
  total_amount: number;
  amount_paid: number;
  amount_outstanding: number;
  status: InvoiceStatus;
  issue_date: string;
  due_date: string;
  paid_date: string | null;
  sent_at: string | null;
  notes: string | null;
  terms: string | null;
  line_items: InvoiceLineItem[];
  created_by: string;
  created_at: string;
  updated_at: string;
  // Tax config snapshot (set at creation; historical documents keep their rate)
  tax_rate_snapshot?: number | null;
  tax_label_snapshot?: string | null;
  tax_inclusive_snapshot?: boolean | null;
  // Online payment provider (Square today; Stripe later). Populated by the
  // square-invoice-send edge function and kept in sync by the webhook.
  payment_provider?: 'square' | 'stripe' | null;
  provider_invoice_id?: string | null;
  provider_order_id?: string | null;
  provider_invoice_status?: string | null;
  payment_url?: string | null;
  provider_synced_at?: string | null;
  amount_refunded?: number | null;
  /** Chargeback state mirrored from the payment provider; null when there is none. */
  dispute_state?: 'open' | 'won' | 'lost' | null;
  dispute_reason?: string | null;
  dispute_amount?: number | null;
  dispute_due_at?: string | null;
  /** Customer signature captured on the technician's device when the job was closed from the app. */
  signature_path?: string | null;
  signed_at?: string | null;
  signer_name?: string | null;
  signature_consent?: boolean | null;
}

export interface InvoiceWithRelations extends Invoice {
  job?: {
    job_number: string;
    job_date?: string | null;
    scheduled_date?: string | null;
    work_summary?: string | null;
    assigned_tech?: { name: string } | null;
  } | null;
  customer?: { customer_name: string; customer_number?: string | null; company_name?: string | null } | null;
  creator?: { full_name: string } | null;
}

/** Embeds every relation a printed invoice needs (job reference, technician, customer number, preparer). */
export const INVOICE_SELECT = `
  *,
  job:jobs(job_number, job_date, scheduled_date, work_summary, assigned_tech:techs!jobs_assigned_tech_id_fkey(name)),
  customer:customers(customer_name, customer_number, company_name),
  creator:profiles!invoices_created_by_fkey(full_name)
`;

export interface InvoiceFilters {
  status?: InvoiceStatus | 'all';
  customerId?: string;
  dateFrom?: Date;
  dateTo?: Date;
  search?: string;
  timezone?: string;
}

export interface InvoiceStats {
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  totalOverdue: number;
  invoiceCount: number;
  draftCount: number;
  sentCount: number;
  paidCount: number;
  overdueCount: number;
  refundedCount: number;
}

export interface CreateInvoiceInput {
  job_id: string;
  customer_id?: string | null;
  customer_name: string;
  customer_email?: string | null;
  customer_phone?: string | null;
  customer_address?: string | null;
  service_address?: string | null;
  subtotal: number;
  gst_amount: number;
  total_amount: number;
  issue_date: string;
  due_date: string;
  notes?: string | null;
  terms?: string | null;
  line_items: InvoiceLineItem[];
}

export interface UpdateInvoiceInput extends Partial<CreateInvoiceInput> {
  id: string;
  status?: InvoiceStatus;
  amount_paid?: number;
  paid_date?: string | null;
  sent_at?: string | null;
  /** Stamped when staff edit the bill-to block; the DB guard then shields it from contact sync. */
  bill_to_edited_at?: string | null;
}
